import { useCallback, useState } from "react";

import { initiateWalletTopUp } from "./paymentsService";
import { mapTopUpInitialization } from "./paymentsMappers";
import { clearPendingTopUp, savePendingTopUp } from "./topUpReturn";
import { confirmTopUp } from "./topUpConfirm";
import { launchPaystack, launchPaystackPopup } from "./paystackLauncher";
import { closePopup, openBlankPopup } from "./paystackPopup";

const useWalletTopUp = () => {
  const [isStarting, setIsStarting] = useState(false);
  const [blocked, setBlocked] = useState(null);

  const settle = useCallback(async (outcome, initiation, pending) => {
    if (outcome === "popup-blocked") {
      setBlocked({ initiation, pending });

      return { status: "popup-blocked" };
    }

    if (outcome === "success" || outcome === "closed") {
      const { confirmed } = await confirmTopUp(pending);

      return { status: confirmed ? "credited" : "pending" };
    }

    clearPendingTopUp();

    return { status: "cancelled" };
  }, []);

  const start = useCallback(
    async ({ walletId, currencyCode, amount, balanceBefore }) => {
      const popup = openBlankPopup();

      setIsStarting(true);

      try {
        const initiation = mapTopUpInitialization(
          await initiateWalletTopUp({ walletId, amount })
        );

        if (!initiation) {
          closePopup(popup);

          throw new Error("Could not start the top-up. Please try again.");
        }

        const pending = { reference: initiation.reference, amount, currencyCode, balanceBefore };

        savePendingTopUp(pending);

        return settle(await launchPaystack(initiation, { popup }), initiation, pending);
      } catch (err) {
        closePopup(popup);

        throw err;
      } finally {
        setIsStarting(false);
      }
    },
    [settle]
  );

  const resumeBlocked = useCallback(async () => {
    if (!blocked) return { status: "invalid" };

    const { initiation, pending } = blocked;

    setBlocked(null);
    setIsStarting(true);

    try {
      return settle(await launchPaystackPopup(initiation), initiation, pending);
    } finally {
      setIsStarting(false);
    }
  }, [blocked, settle]);

  const dismissBlocked = useCallback(() => {
    setBlocked(null);
    clearPendingTopUp();
  }, []);

  const confirm = useCallback((pending) => confirmTopUp(pending), []);

  return {
    isStarting,
    blocked: Boolean(blocked),
    start,
    confirm,
    resumeBlocked,
    dismissBlocked,
  };
};

export default useWalletTopUp;
